import { Close } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material'
import { JSX, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { uploadCertificate } from './uploadCertificate'
import { SerialPortConnection } from './serial'
import CertificateTerminal, { TerminalEntry } from './CertificateTerminal'
import { APP_CONFIG } from '../../../../../env-cmdrc'

const CERTIFICATE_PUBLIC_PATH = `${import.meta.env.BASE_URL}${APP_CONFIG.FOTA_CERT_PATH}`

const USB_BAUD_RATE = 115200
const USB_VENDOR_ID = 0x0483
const USB_PRODUCT_ID = 0x5740

type SerialNavigator = Navigator & {
  serial: {
    requestPort: (options?: {
      filters?: Array<{ usbVendorId?: number; usbProductId?: number }>
    }) => Promise<SerialPortConnection>
  }
}

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  onUploaded?: () => void
}

const stripAnsi = (text: string): string => {
  let result = ''

  for (let index = 0; index < text.length; index += 1) {
    const charCode = text.codePointAt(index) ?? 0
    if (charCode === 27 && text[index + 1] === '[') {
      index += 2
      while (index < text.length) {
        const sequenceCode = text.codePointAt(index) ?? 0
        if (sequenceCode >= 64 && sequenceCode <= 126) {
          break
        }
        index += 1
      }
      continue
    }

    result += text[index]
  }

  return result
}

const maskSensitiveTerminalText = (text: string): string => {
  return text.replace(/(set_shell_debug\s+)(\S+)/gi, '$1******')
}

const UploadCertificateDialog = ({
  open,
  setOpen,
  onUploaded,
}: Props): JSX.Element => {
  const { t } = useTranslation('app')
  const [connecting, setConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isUploaded, setIsUploaded] = useState(false)
  const [showCloseWarning, setShowCloseWarning] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [terminalEntries, setTerminalEntries] = useState<TerminalEntry[]>([])
  const portRef = useRef<SerialPortConnection | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const closePort = useCallback(async () => {
    if (!portRef.current) return
    try {
      await portRef.current.close()
    } catch {
      // Port may already be closed.
    } finally {
      portRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    setIsConnected(false)
    setConnecting(false)
    setIsUploaded(false)
    setErrorMessage(null)
    setTerminalEntries([])
  }, [])

  const closeDialog = useCallback(async () => {
    abortControllerRef.current?.abort()
    await closePort()
    setOpen(false)
    setShowCloseWarning(false)
    reset()
  }, [closePort, setOpen, reset])

  const isProcessRunning = connecting || isConnected

  const requestCloseDialog = useCallback(() => {
    if (isProcessRunning) {
      setShowCloseWarning(true)
      return
    }

    void closeDialog()
  }, [closeDialog, isProcessRunning])

  const handleConfirmClose = useCallback(() => {
    void closeDialog()
  }, [closeDialog])

  const connectToUsbDevice = useCallback(async () => {
    if (!('serial' in navigator)) {
      setErrorMessage(
        t(
          'device.calibration.preFlightStep.certificateDialog.serialNotSupported',
        ),
      )
      return
    }
    reset()

    setConnecting(true)
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    let port: SerialPortConnection | null = null

    try {
      const serialNavigator = navigator as SerialNavigator
      const sanitizeTerminalText = (text: string): string =>
        maskSensitiveTerminalText(stripAnsi(text))

      port = await serialNavigator.serial.requestPort({
        filters: [
          {
            usbVendorId: USB_VENDOR_ID,
            usbProductId: USB_PRODUCT_ID,
          },
        ],
      })
      portRef.current = port
      await port.open({ baudRate: USB_BAUD_RATE })
      if (abortController.signal.aborted) return
      setConnecting(false)
      setIsConnected(true)
      await uploadCertificate(
        port,
        CERTIFICATE_PUBLIC_PATH,
        {
          vendorId: USB_VENDOR_ID,
          productId: USB_PRODUCT_ID,
          baudRate: USB_BAUD_RATE,
        },
        (chunk) => {
          if (abortController.signal.aborted) return
          const text = sanitizeTerminalText(chunk)
          setTerminalEntries((previous) => [
            ...previous,
            { text, source: 'serial' },
          ])
        },
        (message) => {
          if (abortController.signal.aborted) return
          const text = `${sanitizeTerminalText(message)}\n`
          setTerminalEntries((previous) => [
            ...previous,
            { text, source: 'log' },
          ])
        },
        abortController.signal,
      )
      if (abortController.signal.aborted) return
      setIsUploaded(true)
      onUploaded?.()
    } catch (e) {
      if (abortController.signal.aborted) return
      setErrorMessage(
        t('device.calibration.preFlightStep.certificateDialog.uploadError', {
          error: String(e),
        }),
      )
    } finally {
      await closePort()
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
      if (!abortController.signal.aborted) {
        setConnecting(false)
        setIsConnected(false)
      }
    }
  }, [closePort, onUploaded, reset, t])

  let connectButtonLabel = t(
    'device.calibration.preFlightStep.certificateDialog.connectButton',
  )
  if (connecting) {
    connectButtonLabel = t(
      'device.calibration.preFlightStep.certificateDialog.connectingButton',
    )
  } else if (isConnected) {
    connectButtonLabel = t(
      'device.calibration.preFlightStep.certificateDialog.connectedButton',
    )
  }

  return (
    <Dialog
      open={open}
      onClose={requestCloseDialog}
      aria-labelledby="upload-certificate-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <IconButton
          data-testid="upload-certificate-dialog-close"
          onClick={requestCloseDialog}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>

        <Typography
          id="upload-certificate-dialog-title"
          component="h4"
          sx={{ margin: 0, fontSize: '1.25rem', pr: 4 }}
        >
          {t('device.calibration.preFlightStep.certificateDialog.title')}
        </Typography>

        <Typography sx={{ mt: 2 }}>
          {t('device.calibration.preFlightStep.certificateDialog.description')}
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={connectToUsbDevice}
            disabled={connecting || isConnected}
            startIcon={
              connecting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {connectButtonLabel}
          </Button>
        </Box>

        <CertificateTerminal
          terminalEntries={terminalEntries}
          placeholder={t(
            'device.calibration.preFlightStep.certificateDialog.serialOutputPlaceholder',
          )}
        />

        {errorMessage ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}
        {isUploaded ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t(
              'device.calibration.preFlightStep.certificateDialog.uploadSuccess',
            )}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={requestCloseDialog} variant="contained">
          {t('common:general.close')}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showCloseWarning}
        setOpen={setShowCloseWarning}
        title={t(
          'device.calibration.preFlightStep.certificateDialog.closeWarningTitle',
        )}
        confirmText={t(
          'device.calibration.preFlightStep.certificateDialog.closeWarningConfirm',
        )}
        onConfirm={handleConfirmClose}
      >
        {t(
          'device.calibration.preFlightStep.certificateDialog.closeWarningDescription',
        )}
      </ConfirmDialog>
    </Dialog>
  )
}

export default UploadCertificateDialog
